import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Home, Image, Calendar, Star, CreditCard, LogOut, Menu, X, CalendarDays, Bed, Settings, UtensilsCrossed } from 'lucide-react';
import { useState } from 'react';

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('admin_id');
    navigate('/login');
  };

  const navItems = [
    { path: '/admin', icon: Home, label: 'Dashboard' },
    { path: '/admin/rooms', icon: Bed, label: 'Rooms' },
    { path: '/admin/meal-pricing', icon: UtensilsCrossed, label: 'Room + Food Pricing' },
    { path: '/admin/media', icon: Image, label: 'Media Gallery' },
    { path: '/admin/bookings', icon: Calendar, label: 'Bookings' },
    { path: '/admin/calendar', icon: CalendarDays, label: 'Calendar View' },
    { path: '/admin/reviews', icon: Star, label: 'Reviews' },
    { path: '/admin/payment', icon: CreditCard, label: 'Payment Info' },
    { path: '/admin/settings', icon: Settings, label: 'Settings' },
  ];

  const logoUrl = "https://customer-assets.emergentagent.com/job_98d136aa-512d-4c06-970a-a610b1d5ea68/artifacts/gzoc7xht_Primary%20on%20transparent.png";

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar - Desktop */}
      <aside className="hidden md:flex md:flex-col w-64 bg-black text-white">
        <div className="p-4 border-b border-gray-700">
          <img 
            src={logoUrl}
            alt="Himali Khim Homestay" 
            className="h-16 w-full object-contain"
          />
          <p className="text-gray-400 text-xs text-center mt-2">Admin Panel</p>
        </div>
        <nav className="flex-1 p-4 space-y-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-green-600 text-white font-semibold'
                    : 'text-gray-300 hover:bg-gray-800'
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <Icon size={20} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t border-gray-700">
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start text-gray-300 hover:bg-gray-800 hover:text-white"
            data-testid="logout-button"
          >
            <LogOut className="mr-2" size={20} />
            Logout
          </Button>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="fixed top-0 left-0 bottom-0 w-64 bg-black text-white">
            <div className="p-4 border-b border-gray-700 flex justify-between items-center">
              <img 
                src={logoUrl}
                alt="Himali Khim Homestay" 
                className="h-12 object-contain"
              />
              <button onClick={() => setSidebarOpen(false)} className="text-gray-400 hover:text-white" data-testid="close-sidebar-button">
                <X size={24} />
              </button>
            </div>
            <nav className="flex-1 p-4 space-y-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-green-600 text-white font-semibold'
                        : 'text-gray-300 hover:bg-gray-800'
                    }`}
                  >
                    <Icon size={20} />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </nav>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar - Mobile */}
        <header className="md:hidden bg-black border-b border-gray-700 px-4 py-3 flex items-center justify-between">
          <button onClick={() => setSidebarOpen(true)} className="text-white" data-testid="open-sidebar-button">
            <Menu size={24} />
          </button>
          <img 
            src={logoUrl}
            alt="Himali Khim Homestay" 
            className="h-10 object-contain"
          />
          <Button
            onClick={handleLogout}
            variant="ghost"
            size="sm"
            className="text-gray-300 hover:text-white"
          >
            <LogOut size={20} />
          </Button>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-auto p-4 md:p-8" data-testid="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;